import {
  ConfirmDeleteModal,
  DeleteIcon,
  Divider,
  FormControl,
  FormControlLabel,
  GKButton,
  GKClientDataGrid,
  GKDatePicker,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  IconButton,
  Plus1Icon,
  PlusIcon,
  RadioGroup,
  ReloadIcon,
  RightIcon,
  Typography,
} from "components";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import DashboardLayout from "layouts/DashboardLayout";
import { sum } from "lodash";
import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useAppSelector } from "store/store";
import { exportToExcel } from "utils/exportCsv";
import { handleReturnNaturalRows } from "utils/formHelpers";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import { v4 as uuid } from "uuid";
import RebalanceRationale from "./RebalanceRationale";
import VersionUploadModal from "./VersionUploadModal";
import useModelPortfolioVersion from "./useModelPortfolioVersion";

const AddModelportfolioVersionModal = () => {
  const {
    handleAddSubmit,
    insert,
    openUploadModal,
    setOpenUploadModal,
    submitLoading,
    form,
    setTabs,
    handleUpdateVersion,
    isLoading,
    getDataByIdLoading,
    modalPortfolioDelete,
    modelPortdolioData,
    modelPortfolioId,
    replace,
    equityBasketDetailsData,
    totalWeightage,
    setTotalWeightage,
    setOpeRationaleModal,
    opeRationaleModal,
    opeRationaleIndex,
    setOpeRationaleIndex,
  } = useModelPortfolioVersion({ selectedModelPortfolio: null });

  const companies = useAppSelector((state) => state?.app?.companies);

  const [deleteConfirmation, setDeleteConfirmation] = React.useState(false);

  const companyOptions = useMemo(
    () =>
      handleReturnNaturalRows({
        options: companies?.map((item: any) => ({
          ...item,
          name: item?.companyName,
          value: item?.isin,
          gridCode: item?.gridCode,
        })),
        values: form.getValues("stocks"),
        selectedValueKey: "isinCode",
        optionsValueKey: "isin",
      }) || [],
    [companies, form],
  );

  return (
    <DashboardLayout title="Add Model Portfolio" loading={getDataByIdLoading}>
      <Grid2 container spacing={1}>
        <Grid2 size={12}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <form
              id="equity-basket-version-form"
              onSubmit={
                modelPortfolioId
                  ? form?.handleSubmit(handleUpdateVersion)
                  : form?.handleSubmit(handleAddSubmit)
              }
            >
              <Grid2 container spacing={1} size={12}>
                <Grid2 size={3}>
                  <Controller
                    rules={{
                      required: {
                        value: true,
                        message: "Please enter version name",
                      },
                      validate: validateNotWhitespaceOnly,
                    }}
                    control={form.control}
                    name="versionName"
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        inputLabel="Model Portfolio Name"
                        placeholder="Model Portfolio Name"
                        name="versionName"
                        error={Boolean(form.formState.errors?.versionName)}
                        helperText={
                          Boolean(form.formState.errors?.versionName) &&
                          `${form.formState.errors?.versionName?.message}`
                        }
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <Controller
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        onChange={(e) => {
                          field.onChange(dayjs(e).format("YYYY-MM-DD"));
                        }}
                        value={dayjs(field.value)}
                        inputLabel="Model Portfolio start date"
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <Controller
                    control={form.control}
                    disabled={modelPortdolioData?.data?.isARebalance}
                    name="startValue"
                    rules={{
                      min: 1,
                      required: {
                        value: true,
                        message: "Portfolio value should be greater than 0",
                      },
                    }}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        disabled={modelPortdolioData?.data?.isARebalance}
                        inputLabel="Model Portfolio Value"
                        placeholder="Model Portfolio Value"
                        error={Boolean(form.formState.errors?.startValue)}
                        helperText={
                          Boolean(form.formState.errors?.startValue) &&
                          `${form.formState.errors?.startValue?.message}`
                        }
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={3}>
                  <Controller
                    disabled={
                      equityBasketDetailsData?.data?.minimumInvestmentValueCheck
                    }
                    control={form.control}
                    name="minimumInvestement"
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        onChange={(e: any) => {
                          field.onChange(+e.target.value);
                        }}
                        inputLabel="Minimum Invested Value"
                        placeholder="Minimum Invested Value"
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Typography>Weightage Type</Typography>
                  <Controller
                    control={form.control}
                    name="equalWeightage"
                    render={({ field }) => {
                      return (
                        <FormControl
                          sx={{
                            "& .MuiFormGroup-root.MuiRadioGroup-root": {
                              display: "flex",
                              alignItems: "center",
                              flexDirection: "row",
                            },
                          }}
                        >
                          <RadioGroup
                            {...field}
                            onChange={(eve, val) => {
                              field.onChange(val);
                              const equalAllocation =
                                100 / form.getValues("stocks")?.length;
                              const newData = form
                                .getValues("stocks")
                                ?.map((da) => {
                                  return {
                                    ...da,
                                    allocation: JSON.parse(val)
                                      ? equalAllocation
                                      : 0,
                                    weightage: JSON.parse(val)
                                      ? equalAllocation
                                      : 0,
                                  };
                                });

                              form.setValue("stocks", newData);
                            }}
                          >
                            <FormControlLabel
                              value={false}
                              control={<GKRadio />}
                              label="Custom Weighted"
                            />
                            <FormControlLabel
                              value
                              control={<GKRadio />}
                              label="Equal Weighted"
                            />
                          </RadioGroup>
                        </FormControl>
                      );
                    }}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <GKClientDataGrid
                    maxHeight={"calc(100vh - 350px)"}
                    name="addModelPortfoliol"
                    getRowId={(row) => row.uuid}
                    disableRowSelectionOnClick
                    rows={
                      form.getValues("stocks")?.map((d, index) => {
                        return {
                          ...d,
                          uuidIndex: index,
                        };
                      }) || []
                    }
                    columns={[
                      {
                        field: "stock",
                        disableColumnMenu: true,
                        flex: 1,
                        minWidth: 500,
                        valueGetter: (params) => {
                          return params?.row?.companyName || "";
                        },
                        renderCell: (params) => {
                          const index = params.row.uuidIndex;
                          return (
                            <Controller
                              rules={{
                                required: true,
                              }}
                              control={form.control}
                              name={`stocks.${index}.companyName`}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <GKSearchSelect
                                    {...field}
                                    size="small"
                                    disableClearable={false}
                                    sx={{
                                      width: "100%",
                                    }}
                                    options={companyOptions || []}
                                    getOptionLabel={(option) =>
                                      option.name || option
                                    }
                                    renderOption={(props, option) => (
                                      <li
                                        {...props}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 4,
                                        }}
                                      >
                                        <Typography
                                          color={"var(--white)"}
                                          fontSize={8}
                                          style={{
                                            background:
                                              option?.instType === "EQ"
                                                ? "var(--secondary-900)"
                                                : "var(--orange-700)",
                                            padding: "2px 4px",
                                            borderRadius: 4,
                                          }}
                                        >
                                          {option?.instType}
                                        </Typography>
                                        <Typography whiteSpace={"nowrap"}>
                                          {option?.name}
                                        </Typography>
                                      </li>
                                    )}
                                    onChange={(e, val) => {
                                      form.setValue(`stocks.${index}`, {
                                        ...form.getValues(`stocks.${index}`),
                                        cap: val?.cap,
                                        gridCode: val?.gridCode,
                                        companyName: val?.name,
                                        sector: val?.sector,
                                        isinCode: val?.value,
                                      });
                                    }}
                                    error={!!error}
                                  />
                                );
                              }}
                            />
                          );
                        },
                      },
                      {
                        disableColumnMenu: true,
                        field: "weightage",
                        flex: 1,
                        headerName: `Weightage`,
                        type: "number",
                        valueGetter: (params) => {
                          return params?.row?.allocation
                            ? +params?.row?.allocation
                            : 0;
                        },
                        renderCell: (params) => {
                          const index = params.row.uuidIndex;

                          return (
                            <Controller
                              control={form.control}
                              name={`stocks.${index}.allocation`}
                              rules={{
                                required: true,
                              }}
                              render={({ field, fieldState: { error } }) => {
                                return form.getValues("equalWeightage") ===
                                  "true" ? (
                                  <Typography>
                                    {form
                                      ?.getValues(`stocks.${index}.allocation`)
                                      ?.toFixed(2)}
                                  </Typography>
                                ) : (
                                  <GKTextField
                                    {...field}
                                    size="small"
                                    name={`stocks.${index}.allocation`}
                                    type="number"
                                    value={form.getValues(
                                      `stocks.${index}.allocation`,
                                    )}
                                    placeholder="Weightage"
                                    onChange={(e) => {
                                      form.setValue(`stocks.${index}`, {
                                        ...form.getValues(`stocks.${index}`),
                                        allocation: +e.target.value,
                                        weightage:
                                          (+form.getValues("startValue") /
                                            +e.target.value) *
                                          100,
                                      });
                                    }}
                                    error={!!error}
                                  />
                                );
                              }}
                            />
                          );
                        },
                      },
                      {
                        field: "rationale",
                        headerName: "rationale",
                        disableColumnMenu: true,
                        sortable: false,
                        flex: 1,
                        renderCell: (params) => {
                          const index = params.row.uuidIndex;
                          return (
                            <GKButton
                              size="small"
                              onClick={() => {
                                setOpeRationaleIndex(index);
                                setOpeRationaleModal(true);
                              }}
                              endIcon={
                                form.getValues(`stocks.${index}.rationale`) && (
                                  <RightIcon />
                                )
                              }
                              startIcon={
                                !form.getValues(
                                  `stocks.${index}.rationale`,
                                ) && <PlusIcon />
                              }
                            >
                              {form.getValues(`stocks.${index}.rationale`)
                                ? "View"
                                : "Add Rationale"}
                            </GKButton>
                          );
                        },
                      },
                      {
                        field: "action",
                        headerName: "Actions",
                        disableColumnMenu: true,
                        align: "right",
                        flex: 1,
                        headerAlign: "right",
                        sortable: false,
                        renderCell: (params) => {
                          const index = params.row.uuidIndex;
                          return (
                            <IconButton
                              disabled={form.getValues("stocks").length === 1}
                              onClick={() => {
                                const equalAllocation =
                                  100 /
                                  form
                                    .getValues("stocks")
                                    ?.filter((d, i) => i !== index)?.length;

                                const updatedData = JSON.parse(
                                  form.getValues("equalWeightage"),
                                )
                                  ? form
                                      .getValues("stocks")
                                      ?.filter((d, i) => i !== index)
                                      ?.map((da) => {
                                        return {
                                          ...da,
                                          allocation: equalAllocation,
                                          weightage: equalAllocation,
                                        };
                                      })
                                  : form
                                      .getValues("stocks")
                                      ?.filter((d, i) => i !== index);

                                form.setValue("stocks", updatedData);
                              }}
                              color="error"
                              className="outline"
                            >
                              <DeleteIcon width={18} />
                            </IconButton>
                          );
                        },
                      },
                    ]}
                    headerComponent={
                      <Grid2
                        container
                        justifyContent={"flex-end"}
                        alignItems={"center"}
                        gap={1}
                      >
                        <Typography fontSize={12} color={"var(--grey-700)"}>
                          Total Weightage:{"  "}
                          <span
                            style={{
                              fontWeight: 600,
                              color: "var(--grey-900)",
                            }}
                          >
                            {+totalWeightage?.toFixed(2)}
                          </span>
                        </Typography>
                        <IconButton
                          onClick={() => {
                            setTotalWeightage(
                              sum(
                                form
                                  .getValues("stocks")
                                  ?.map((d) => +d?.allocation),
                              ),
                            );
                          }}
                        >
                          <ReloadIcon
                            color="var(--primary-900)"
                            width={14}
                            height={14}
                          />
                        </IconButton>
                        <Divider orientation="vertical" sx={{ height: 24 }} />
                        <GKButton
                          size="small"
                          onClick={() => {
                            insert(0, {
                              isinCode: "",
                              allocation: null,
                              targetPrice: null,
                              stopLoss: null,
                              weightage: null,
                              rationale: "",
                              sector: "",
                              gridCode: "",
                              cap: "",
                              companyName: "",
                              uuid: uuid(),
                            });
                            if (JSON.parse(form.getValues("equalWeightage"))) {
                              const equalAllocation =
                                100 / form.getValues("stocks")?.length;
                              const newData = form
                                .getValues("stocks")
                                ?.map((da) => {
                                  return {
                                    ...da,
                                    allocation: equalAllocation,
                                    weightage: equalAllocation,
                                  };
                                });
                              replace(newData);
                            }
                          }}
                          startIcon={<Plus1Icon width={14} height={14} />}
                        >
                          Add Stock
                        </GKButton>
                        <GKButton
                          onClick={() =>
                            exportToExcel(
                              form.getValues("stocks")?.map((d) => {
                                return {
                                  "Company Name": d?.companyName,
                                  "Allocated Weightage": d?.allocation,
                                  Cap: d?.cap,
                                };
                              }),
                              `${form.getValues("versionName")}_Model_Portfolio`,
                            )
                          }
                        >
                          Download Excel
                        </GKButton>
                      </Grid2>
                    }
                  />
                </Grid2>
                <Grid2
                  size={12}
                  p={2}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                  gap={2}
                  zIndex={22}
                >
                  <GKButton
                    variant="contained"
                    type="submit"
                    size="medium"
                    form="equity-basket-version-form"
                    loading={submitLoading || isLoading}
                  >
                    {"Submit"}
                  </GKButton>
                  <GKButton
                    variant="outlined"
                    onClick={() => {
                      setOpenUploadModal(true);
                      setTabs(0);
                    }}
                  >
                    Upload Model
                  </GKButton>
                </Grid2>
              </Grid2>
            </form>
          </motion.div>
        </Grid2>
      </Grid2>

      {openUploadModal && (
        <VersionUploadModal
          open={openUploadModal}
          form={form}
          setOpen={setOpenUploadModal}
        />
      )}

      {opeRationaleModal && (
        <RebalanceRationale
          open={opeRationaleModal}
          index={opeRationaleIndex}
          form={form}
          setOpen={setOpeRationaleModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmDeleteModal
          open={deleteConfirmation}
          setOpen={setDeleteConfirmation}
          onDelete={() => modalPortfolioDelete(modelPortfolioId)}
          title="Model Portfolio"
          isDelete
        />
      )}
    </DashboardLayout>
  );
};

export default AddModelportfolioVersionModal;
